'use client'

import React from 'react'
import Link from 'next/link'
import { Flex, Text } from '@mantine/core'
import type { WholeBodyCarouselFirstCard } from '@/lib/hardcoded-data/CategoryPages/types'
import type { StaticImageData } from 'next/image'
import type { ServiceItem } from '@/types/interfaces/service-item'
import { getServiceHref } from '@/lib/getServiceHref'
import { useUserContext } from '@/contexts/UserContext'
import { usePrescriptionRenewalStorefrontNavigation } from '@/hooks/usePrescriptionRenewalStorefrontNavigation'
import {
    isPrescriptionRenewalStorefrontSkuId,
    PRESCRIPTION_RENEWAL_HREF,
} from '@/types/enums/sku-constants'

interface FeaturedCarouselCardV2Props {
    data: WholeBodyCarouselFirstCard
    bgImage: StaticImageData | string
    /** When provided, pricing and link come from backend service; title/description from data */
    service?: ServiceItem
}

export default function FeaturedCarouselCardV2({ data, bgImage, service }: FeaturedCarouselCardV2Props) {
    const { user } = useUserContext()
    const catalogHref = service ? getServiceHref(service) : data?.buttonLink
    const isPrescriptionRenewal =
        isPrescriptionRenewalStorefrontSkuId(service?.skuId) || catalogHref === PRESCRIPTION_RENEWAL_HREF
    const { href: prescriptionRenewalHref, isNavigating, navigateToPrescriptionRenewal } =
        usePrescriptionRenewalStorefrontNavigation(!!user && isPrescriptionRenewal)
    const href = (isPrescriptionRenewal ? prescriptionRenewalHref : catalogHref) ?? '#'
    const bgSrc = typeof bgImage === 'string' ? bgImage : bgImage.src
    const primaryPrice = service ? service.primaryPrice : data?.primaryPrice
    const secondaryPrice = service ? service.secondaryPrice : data?.secondaryPrice

    return (
        <Link
            href={href}
            aria-busy={isPrescriptionRenewal && isNavigating ? true : undefined}
            style={{ textDecoration: 'none' }}
            onClick={async (event) => {
                if (isPrescriptionRenewal) {
                    await navigateToPrescriptionRenewal(event)
                }
            }}
        >
            <Flex
                w={283}
                h={{ base: 340, sm: 350 }}
                direction="column"
                justify="space-between"
                p={32}
                style={{
                    borderRadius: 16,
                    backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 100%), url(${bgSrc})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'right',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <Text
                    component="span"
                    bg="rgba(31, 30, 29, 0.8)"
                    px={12}
                    py={6}
                    size="sm"
                    fw={500}
                    c="white"
                    style={{ borderRadius: 20, width: 'fit-content' }}
                >
                    Specialist review
                </Text>
                <Flex direction="column" gap={12}>
                    <Text
                        component="h2"
                        c="white"
                        m={0}
                        fz="var(--gm-refresh-title-card)"
                        fw={450}
                        lh="110%"
                        lts={-0.28}
                    >
                        {data.title}
                    </Text>
                    <Text component="p" c="white" m={0} size="md" fw={400} lh="130%" opacity={0.95} lts={-0.32}>
                        {data.description}
                    </Text>
                    {(primaryPrice || secondaryPrice) && (
                        <Flex direction="column" gap={4}>
                            {primaryPrice && (
                                <Text c="white" m={0} size="sm" fw={550} lh="130%" opacity={0.95}>
                                    {primaryPrice}
                                </Text>
                            )}
                            {secondaryPrice && (
                                <Text c="white" m={0} size="sm" fw={400} lh="130%" opacity={0.85}>
                                    {secondaryPrice}
                                </Text>
                            )}
                        </Flex>
                    )}
                </Flex>
            </Flex>
        </Link>
    )
}
